import get from "lodash/get";
import { array, object, string } from "prop-types";
import React, { useEffect, useState } from "react";
import { StoryCard } from "../../../arrow/components/Molecules/StoryCard";
import LiveButton from "../../atoms/live-button";
import { useDeviceType } from "../../customHooks/useDeviceType";
import { Newsletter } from "../../molecules/Newsletter";
import { getCategoryAndSubCategory, getTemplateName, useTrackWidgetImpression } from "../../../ga-utils";
import YouTube from "react-youtube";
import ImagewithAnchor from "../../../arrow/components/Molecules/ImagewithAnchor";
import { BrowserView, MobileView } from "react-device-detect";

import "./four-col-ten-stories-budget.m.css";
import { Headline } from "../../../arrow/components/Atoms/Headline";
import { AuthorWithTime } from "../../../arrow/components/Atoms/AuthorWithTimestamp";
import { Subheadline } from "../../../arrow/components/Atoms/Subheadline";
import { Link } from "@quintype/components";

const YoutubeIframe = ({ collection }) => {
  const onPlayerReady = (event) => {
    event.target.playVideo();
  };

  return (
    <div styleName="yt-container">
      <YouTube
        id="videoFrame"
        videoId={collection.summary}
        className="yt-iframe"
        opts={{
          width: "100%",
          height: "100%",
          playerVars: {
            autoplay: 1,
            mute: 1,
            controls: 1,
            rel: 0,
            modestbranding: 1,
            playsinline: 1,
          },
        }}
        onReady={onPlayerReady}
      />
    </div>
  );
};

export const FourColTenStoriesBudget = (props) => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const { collection, stories, config, data, pageType } = props;
  const { magazineWidget } = data;
  if (stories.length < 0) {
    return null;
  }

  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const [apiTitles, setApiTitles] = useState([]);
  const [apiTimes, setApiTimes] = useState([]);
  const { enable_newsletter = false } = get(collection, ["associated-metadata"], {});
  const isLiveBlog = get(collection, ["items", [0], "story", "story-template"], "") === "live-blog";
  const liveBlogStoryId = get(stories, [0, "id"], null);
  const budgetBannerDesktopImageUrl = get(
    collection,
    ["associated-metadata", "budget_banner_desktop_image_url"],
    false
  );
  const budgetBannerDesktopImageUrlCta = get(
    collection,
    ["associated-metadata", "budget_banner_desktop_image_url_cta"],
    false
  );
  const budgetBannerMobileImageUrl = get(collection, ["associated-metadata", "budget_banner_mobile_image_url"], false);
  const budgetBannerMobileImageUrlCta = get(
    collection,
    ["associated-metadata", "budget_banner_mobile_image_url_cta"],
    false
  );
  const budgetBlackTheme = get(collection, ["associated-metadata", "budget_black_theme"], false);
  const showDesktopBudgetBanner = budgetBannerDesktopImageUrl && budgetBannerDesktopImageUrlCta;
  const showMobileBudgetBanner = budgetBannerMobileImageUrl && budgetBannerMobileImageUrlCta;

  const isAdfreePage = get(config, ["subscriber"]) === "true";
  const showNewsletter = enable_newsletter && !isAdfreePage;

  const secondRowStartIndex = showNewsletter ? 1 : 2;
  const thirdRowStartIndex = showNewsletter ? 5 : 6;

  // Set up widget impression tracking
  const isBrowser = typeof window !== "undefined";
  const componentRef = useTrackWidgetImpression(
    {
      sectionName: collection?.name || "Four Col Ten Stories",
      pageType: pageType,
      location: isBrowser ? window.location : null,
      config: config,
      threshold: 0.2,
    },
    { disabled: !isBrowser }
  );

  const noAdStoryStyle = { borderBottom: stories.length > 2 || "none", paddingBottom: stories.length > 2 || 0 };
  const handleStoryClick = (story, position) => {
    if (typeof window !== "undefined" && window.dataLayer) {
      const storyUrl = `/${story.slug}`;
      const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType);
      window.dataLayer.push({
        event: "dl_story_listing_component_click",
        link_position: position,
        section_name: collection?.name?.toLowerCase(),
        category: category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: getTemplateName(pageType),
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
        eventCallback: function () {
          window.location.href = storyUrl;
        },
      });
    }
  };

  function stripHTML(html) {
    if (!html) return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  }

  useEffect(() => {
    if (!isLiveBlog || !liveBlogStoryId) return;

    let mounted = true;

    const fetchStory = async () => {
      try {
        const res = await fetch(`${config["sketches-host"]}/api/v1/stories/${liveBlogStoryId}`);
        const json = await res.json();

        const cardsFromApi = get(json, ["story", "cards"], []);
        const isClosed = get(json, ["story", "metadata", "is-closed"]) === true;

        const cards = isClosed ? [...cardsFromApi].reverse() : cardsFromApi;

        const titles = [];
        const times = [];

        cards.forEach((card) => {
          const elements = card["story-elements"] || [];
          elements.forEach((el) => {
            if ((el.type === "title" || el.type === "text") && el.text) {
              titles.push(stripHTML(el.text.trim()));
              times.push(card["card-event-at"] || null);
            }
          });
        });

        if (mounted) {
          setApiTitles(titles);
          setApiTimes(times);
        }
      } catch (e) {
        console.error("Story API error", e);
      }
    };

    fetchStory();

    return () => {
      mounted = false;
    };
  }, [isLiveBlog, liveBlogStoryId]);

  const displayTitles = isDesktop ? apiTitles.slice(0, 1) : apiTitles.slice(0, 2);

  const displayTimes = isDesktop ? apiTimes.slice(0, 1) : apiTimes.slice(0, 2);

  return (
    <div styleName={`${budgetBlackTheme ? "black-theme" : ""} four-col-ten-stories`} ref={componentRef}>
      {budgetBannerDesktopImageUrl && budgetBannerMobileImageUrl && (
      <div styleName="budget-banner">
      {isClient && (
        <>
          {showDesktopBudgetBanner && (
            <BrowserView>
                <a href={budgetBannerDesktopImageUrlCta}>
                  <img
                    src={budgetBannerDesktopImageUrl}
                    width={1140}
                    height={150}
                    alt="Budget 2026"
                    title="Budget 2026"
                    loading="lazy"
                  />
                </a>
            </BrowserView>
          )}

          {showMobileBudgetBanner && (
            <MobileView>
                <a href={budgetBannerMobileImageUrlCta}>
                  <img
                    src={budgetBannerMobileImageUrl}
                    width={360}
                    height={100}
                    alt="Budget 2026"
                    title="Budget 2026"
                    loading="lazy"
                  />
                </a>
            </MobileView>
          )}
        </>
      )}
      </div>
)}

      <div styleName="main-story-container">
        <div styleName="main-story" onClick={() => handleStoryClick(stories[0], 1)}>
          {collection.summary && <YoutubeIframe collection={collection}></YoutubeIframe>}
        </div>
        <div styleName="second-story">
          {/* <ImagewithAnchor DefaultMainCard isFeaturedImage={true} showAuthorContent={false} story={stories[0]} config={config} aspectRatio={[[16, 9]]} mainCardImg /> */}
          <StoryCard story={stories[0]} config={config}>
            <div styleName="liveblog-card">
              <LiveButton story={stories[0]} />
                <Link href={`/${stories[0].slug}`} disableAjaxLinks={true}>
                  <div styleName={`${isLiveBlog ? "" : "normal-card-content"} liveblog-card-content`}>
                    <Headline story={stories[0]} premiumStoryIconConfig={config} />
                    </div>
                    <div styleName={`liveblog-home-wrapper ${isLiveBlog ? "" : "normal-home-wrapper"}`}>
                      {isLiveBlog &&
                        displayTitles.map((title, idx) => (
                          <React.Fragment key={idx}>
                            <div styleName="liveblog-timestamp">
                              <svg
                                class="live-icon"
                                xmlns="http://www.w3.org/2000/svg"
                                width="25"
                                height="24"
                                viewBox="0 0 25 24"
                              >
                                <path
                                  fill="#C6554F"
                                  d="M12 18a6 6 0 110-12 6 6 0 010 12zm0 2.5a8.5 8.5 0 110-17 8.5 8.5 0 010 17zm0-1a7.5 7.5 0 100-15 7.5 7.5 0 000 15z"
                                />
                              </svg>

                              <AuthorWithTime
                                config={config}
                                story={
                                  displayTimes[idx] ? { ...stories[0], "last-published-at": displayTimes[idx] } : stories[0]
                                }
                                hideAuthorImage={true}
                                showAuthorContent={false}
                              />
                            </div>
                            <Subheadline story={{ ...stories[0], subheadline: title }} truncateChars={140} />
                          </React.Fragment>
                        ))}
                      {!isLiveBlog && <Subheadline story={stories[0]} truncateChars={140} />}
                      <div styleName={`${isLiveBlog ? "" : "normal-story-link"} live-blog-link-wrapper`}>
                        <span styleName="live-blog-link">
                          {isLiveBlog ? "More Updates" : "Read More"}
                        </span>
                      </div>
                    </div>
                </Link>
            </div>
          </StoryCard>
        </div>
        {showNewsletter ? (
          <Newsletter collection={collection} config={config} magazineWidget={magazineWidget} budgetTheme={false} />
        ) : (
          stories.length > 1 && (
            <div
              styleName="story-card right-column-story"
              style={noAdStoryStyle}
              onClick={() => handleStoryClick(stories[1], 2)}
            >
              {/* <StoryCard story={stories[1]} config={config} aspectRatio={[[16, 9]]} showMultipleAuthor={true} showAuthorContent={false} addFreeCard={true} /> */}
              <ImagewithAnchor
                story={stories[1]}
                config={config}
                aspectRatio={[[16, 9]]}
                showMultipleAuthor={true}
                showAuthorContent={false}
                addFreeCard={true}
              />
            </div>
          )
        )}
      </div>
      {stories.length > secondRowStartIndex && (
        <div styleName="story-grid-container">
          {stories.slice(secondRowStartIndex, secondRowStartIndex + 4).map((story, index) => {
            const position = secondRowStartIndex + index + 1;
            return (
              <div styleName="story-card" key={story.id} onClick={() => handleStoryClick(story, position)}>
                <ImagewithAnchor
                  story={story}
                  isHorizontalWithImageLast={!isDesktop}
                  config={config}
                  aspectRatio={[[16, 9]]}
                  showAuthorContent={false}
                  isHidesubheadline={true}
                />
                {/* <StoryCard
                  isHorizontalWithImageLast={!isDesktop}
                  story={story}
                  config={config}
                  aspectRatio={[[16, 9]]}
                  showMultipleAuthor={true}
                  showAuthorContent={false}
                /> */}
              </div>
            );
          })}
        </div>
      )}
      {stories.length > thirdRowStartIndex && (
        <div styleName="story-grid-container">
          {stories.slice(thirdRowStartIndex, thirdRowStartIndex + 4).map((story, index) => {
            const position = thirdRowStartIndex + index + 1;
            return (
              <div styleName="story-card" key={story.id} onClick={() => handleStoryClick(story, position)}>
                <ImagewithAnchor
                  story={story}
                  isHorizontalWithImageLast={!isDesktop}
                  config={config}
                  aspectRatio={[[16, 9]]}
                  showAuthorContent={false}
                />
                {/* <StoryCard
                  isHorizontalWithImageLast={!isDesktop}
                  story={story}
                  config={config}
                  aspectRatio={[[16, 9]]}
                  showMultipleAuthor={true}
                  showAuthorContent={false}
                /> */}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

FourColTenStoriesBudget.propTypes = {
  collection: object,
  stories: array,
  config: object,
  data: object,
  pageType: string,
};

FourColTenStoriesBudget.storyLimit = 20;
